import React from "react";
import {
  FileText,
  Loader,
  Loader2,
  Download,
  ShieldCheck,
  Layers,
  Target,
  Calculator,
  Activity,
  BarChart,
  BarChart4,
  Award,
  Zap,
  DollarSign,
  RotateCcw,
  TrendingUp,
  History,
  XCircle,
  Shield,
  AlertTriangle,
  Skull,
  AlertCircle,
  Scale,
  Table,
  RefreshCw,
  Info,
  Briefcase,
} from "lucide-react";
import { cn, coerceToText } from "../../lib/utils";
import { formatNumber, safeToFixed } from "../../lib/formatters";
import type { CompanyAnalysis, MarketContext } from "../../types/geminiTypes";
import type { DcfModel } from "../../hooks/useDcfModel";
import { OracleDetailSection } from "./OracleDetailSection";
import { MarketIntelligencePanel } from "./MarketIntelligencePanel";
import ReactMarkdown from "react-markdown";
import { Tooltip } from "../Tooltip";
import {
  SectionHeader,
  Callout,
  DarkEmphasisCard,
  MetricStrip,
  StackedNarrativeCard,
  NarrativeGrid,
} from "./MemoPrimitives";

export interface MemoTabProps {
  analysis: CompanyAnalysis;
  dcfModel: DcfModel;
  marketContext: MarketContext | null;
  memoRef: React.RefObject<HTMLDivElement>;
  isExporting: boolean;
  onExport: () => void;
}

// Scalar narrative fields that MemoTab renders as raw React children. Each is
// typed as `string` but the Oracle occasionally emits a { title, description }
// object; an object child throws React error #31. We coerce them at render time
// so existing saved memos (which already contain the bad shape) render safely.
const MEMO_SCALAR_TEXT_FIELDS = [
  "summary",
  "killerInsight",
  "valuationRationale",
  "valuationInsights",
  "buyZoneJustification",
  "allocationGuidance",
  "cycleSensitivityPenalty",
  "throughCycleAssumptions",
  "longTermOutlook",
  "tenYearHistoricalContext",
  "historicalFailuresContext",
  "technicalDifferentiation",
  "lollapaloozaEffects",
  "whyNotPerfect",
  "normalizedEarnings",
  "capitalAllocationDecision",
] as const;

function normalizeAnalysisForRender(a: CompanyAnalysis): CompanyAnalysis {
  if (!a || typeof a !== "object") return a;
  const out: any = { ...a };

  // Top-level scalar string fields.
  for (const k of MEMO_SCALAR_TEXT_FIELDS) {
    const v = out[k];
    if (v != null && typeof v !== "string") out[k] = coerceToText(v);
  }

  // Nested scalar string fields rendered raw.
  if (
    out.optionalityMultiplier &&
    typeof out.optionalityMultiplier === "object" &&
    typeof out.optionalityMultiplier.description !== "string"
  ) {
    out.optionalityMultiplier = {
      ...out.optionalityMultiplier,
      description: coerceToText(out.optionalityMultiplier.description),
    };
  }
  if (
    out.businessQualityVsInvestmentAttractiveness &&
    typeof out.businessQualityVsInvestmentAttractiveness === "object"
  ) {
    const bq = out.businessQualityVsInvestmentAttractiveness;
    out.businessQualityVsInvestmentAttractiveness = {
      ...bq,
      businessQuality:
        typeof bq.businessQuality === "string"
          ? bq.businessQuality
          : coerceToText(bq.businessQuality),
      investmentAttractiveness:
        typeof bq.investmentAttractiveness === "string"
          ? bq.investmentAttractiveness
          : coerceToText(bq.investmentAttractiveness),
    };
  }
  if (out.expectedReturnMath && typeof out.expectedReturnMath === "object") {
    const e = out.expectedReturnMath;
    out.expectedReturnMath = {
      ...e,
      impliedGrowth:
        typeof e.impliedGrowth === "string"
          ? e.impliedGrowth
          : coerceToText(e.impliedGrowth),
      requiredIrr:
        typeof e.requiredIrr === "string"
          ? e.requiredIrr
          : coerceToText(e.requiredIrr),
      realisticIrr:
        typeof e.realisticIrr === "string"
          ? e.realisticIrr
          : coerceToText(e.realisticIrr),
      rationale:
        typeof e.rationale === "string"
          ? e.rationale
          : coerceToText(e.rationale),
    };
  }

  // archetype.category and archetype.rationale are rendered as raw React children.
  // If the model returns either as an object, coerce to string to prevent error #31.
  if (out.archetype && typeof out.archetype === "object") {
    out.archetype = {
      ...out.archetype,
      category:
        typeof out.archetype.category === "string"
          ? out.archetype.category
          : coerceToText(out.archetype.category),
      rationale:
        typeof out.archetype.rationale === "string"
          ? out.archetype.rationale
          : coerceToText(out.archetype.rationale),
    };
  }

  return out as CompanyAnalysis;
}

export function MemoTab({
  analysis: analysisProp,
  dcfModel,
  marketContext,
  memoRef,
  isExporting,
  onExport,
}: MemoTabProps) {
  // Defensive: coerce raw-rendered scalar narrative fields once so a stray
  // { title, description } object (incl. in already-saved memos) cannot crash
  // the memo render as React error #31.
  const analysis = React.useMemo(
    () => normalizeAnalysisForRender(analysisProp),
    [analysisProp],
  );
  const engineBase = (analysis as any)?.scenarios?.base;
  const engineProjections: { year: number; oe: number; pv: number }[] =
    engineBase?.projectedOwnerEarnings?.map((oe: number, i: number) => ({
      year: i + 1,
      oe,
      pv: engineBase?.presentValues?.[i] ?? 0,
    })) ?? [];

  let downsidePct = 0;
  let upsidePct = 0;
  let asymmetryRatio: number | null = null;
  let bearScenarioWeight = "Medium";
  let bullScenarioWeight = "Medium";
  let showAsymmetry = false;

  if (dcfModel?.isEstimable && !analysis?.isUnresolvableValuation && analysis?.currentPrice && analysis.currentPrice > 0) {
    downsidePct = (dcfModel.scenarios.bear - analysis.currentPrice) / analysis.currentPrice;
    upsidePct = (dcfModel.scenarios.bull - analysis.currentPrice) / analysis.currentPrice;
    bearScenarioWeight = analysis.valuationScenarios?.find((s: any) => s.scenario === "Bear")?.probability || "Medium";
    bullScenarioWeight = analysis.valuationScenarios?.find((s: any) => s.scenario === "Bull")?.probability || "Medium";
    if (Math.abs(downsidePct) >= 0.005) {
      asymmetryRatio = upsidePct / Math.abs(downsidePct);
    }
    showAsymmetry = true;
  }

  if (
    engineProjections.length > 0 &&
    engineProjections[0].pv === 0 &&
    analysis?.valuationBridge?.discountRate
  ) {
    const wacc =
      parseFloat(
        analysis.valuationBridge.discountRate.replace("%", "").trim(),
      ) / 100;
    if (!isNaN(wacc) && wacc > 0) {
      engineProjections.forEach((row, i) => {
        row.pv = row.oe / Math.pow(1 + wacc, i + 1);
      });
    }
  }

  const engineSumOfPVs = engineBase?.sumOfPVs ?? null;
  const enginePvTerminal = engineBase?.pvOfTerminalValue ?? null;
  const engineTotalEquity = engineBase?.totalEquityValue ?? null;

  // RI‑3 display: when the engine set the Type‑4 IV via the deterministic
  // residual‑income model, render the RI walk (additive: tangible book anchor +
  // ΣPV(RI) + PV continuing = RI IV) instead of the discarded live‑DCF projection.
  const riWalk: Array<{
    year: number;
    beginningBook: number;
    rotce: number;
    residualIncome: number;
    pvResidualIncome: number;
  }> = Array.isArray((engineBase as any)?.__riWalk)
    ? (engineBase as any).__riWalk
    : [];
  const isRiWalk =
    (engineBase as any)?.__ivMethod === "residual-income" && riWalk.length > 0;
  const riAnchorPerShare = (engineBase as any)?.__riTangibleBookAnchorPerShare ?? null;
  const riSumPvPerShare = (engineBase as any)?.__riSumPvResidualPerShare ?? null;
  const riPvContinuingPerShare = (engineBase as any)?.__riPvContinuingPerShare ?? null;
  const riValuePerShare = (engineBase as any)?.__riValuePerShare ?? null;
  const riKeUsedPct = (engineBase as any)?.__riKeUsedPct ?? null;
  const riOmega = (engineBase as any)?.__riOmega ?? null;
  // [SPEC-1] Deterministic CURRENT market multiple (price ÷ tangible book/sh) and the
  // RI-implied FAIR multiple. Rendered as chrome so the memo states each figure from a
  // single engine-computed source — prose must never derive a P/TBV ratio.
  const type4CurrentPtbv = (engineBase as any)?.__type4CurrentPtbv ?? null;
  const type4RiImpliedPtbv =
    (analysis as any)?.observability?.type4RiImpliedPtbv ?? null;
  // [SPEC-3] Type-4 peer table renders explicitly named fields under their correct
  // labels. Subject ROTCE is the engine's corrected RI ROTCE (already a percentage).
  const isType4Entity =
    analysis.entityType === "Financial" || analysis.entityType === "Type 4";
  const type4SubjectRotcePct =
    (analysis as any)?.observability?.type4RiRotceUsed ??
    (analysis as any)?.observability?.type4RotceBasePct ??
    null;
  // [SPEC-2] Deterministic investment composition ($MM), sourced from the filed
  // balance sheet. Rendered as chrome so the investment-engine figure is labeled by
  // composition and never restated as a bare "equity portfolio" in prose.
  const type4InvestmentComposition =
    (analysis as any)?.observability?.type4InvestmentComposition ?? null;
  // [SPEC-9] The rate tangible book actually compounds at in the RI walk
  // (rotce × retention, ~13%/yr for BRK-B) is a DIFFERENT quantity from the
  // scenario base growth input (~7.5%) — the memo wore the word "growth" for
  // both. Measured deterministically by [GROWTH-LABEL-GAP] (Spec 4); rendered
  // here so neither figure is left as an unqualified "growth" number. The two
  // are supposed to differ and are never reconciled.
  const riImpliedBookGrowthPct =
    (analysis as any)?.observability?.riImpliedBookGrowthPct ?? null;
  const riClearedUnresolvable = (engineBase as any)?.__riClearedUnresolvable === true;

  const formatCurrencyValue = (millions: number | undefined | null) => {
    if (millions === undefined || millions === null || isNaN(millions))
      return "N/A";
    if (Math.abs(millions) >= 1000) {
      return "$" + (millions / 1000).toFixed(1) + "B";
    }
    return "$" + Math.round(millions) + "M";
  };

  // (S5) Owner-earnings projection precision. The $0.1B rounding used elsewhere
  // collapses adjacent fade years to identical labels (e.g. Yr2/Yr3 both "$0.4B")
  // even though the underlying series fades ~22%→~10%. Render each year with
  // enough precision that the fade is monotonic and no two adjacent years
  // collide: sub-$1B in whole $M, ≥$1B in $0.00B. Values are in $millions.
  const formatProjectionValue = (millions: number | undefined | null) => {
    if (millions === undefined || millions === null || isNaN(millions))
      return "N/A";
    return Math.abs(millions) >= 1000
      ? "$" + (millions / 1000).toFixed(2) + "B"
      : "$" + Math.round(millions) + "M";
  };

  const stripValuationGap = (text: string | null | undefined) => {
    if (!text) return text;
    // Replace the exact repeated phrase with a shorter numeric reference
    return text.replace(
      /(?:~?\$[\d,.]+\s+vs\.?\s+~?\$[\d,.]+\s+intrinsic value,?\s+)?(~?[\d,.]+%?\s+premium),?\s+the math does not work to justify the entry price\.?/gi,
      "($1 to IV)",
    );
  };

  return (
    <>
      <section className="oi-card p-4 sm:p-8 mt-4 sm:mt-8">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 sm:mb-8 gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-md bg-oxblood flex items-center justify-center">
              <FileText className="w-6 h-6 text-paper" />
            </div>
            <div>
              <h3 className="font-serif text-3xl font-medium italic tracking-tight text-ink">
                Investment Memo
              </h3>
              <p className="text-[10px] font-medium uppercase tracking-[0.2em] text-ink-500 mt-1">
                Institutional Grade Analysis
              </p>
            </div>
          </div>
          <button
            onClick={onExport}
            disabled={isExporting}
            className="w-full sm:w-auto px-6 py-3 bg-oxblood text-paper rounded-md text-[10px] font-medium uppercase tracking-[0.2em] hover:bg-oxblood-800 flex items-center justify-center sm:justify-start gap-3 transition-all disabled:opacity-50"
          >
            {isExporting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            <span>
              {isExporting ? "Exporting Report..." : "Export PDF Report"}
            </span>
          </button>
        </div>

        <div className="md:hidden mb-4 p-4 tone-warning rounded-md flex items-start gap-3">
          <div className="shrink-0 mt-0.5">
            <svg
              className="w-4 h-4 text-warning-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <div>
            <p className="text-xs font-medium uppercase tracking-wider mb-1 opacity-80">
              Desktop Recommended
            </p>
            <p className="text-xs opacity-70 leading-relaxed">
              The Investment Memo is optimized for desktop viewing and PDF
              export. The PDF download is identical on all devices.
            </p>
          </div>
        </div>

        <div
          ref={memoRef}
          className={cn(
            "prose prose-lg max-w-none text-ink-600 leading-relaxed",
            isExporting && "export-mode",
          )}
        >
          <div className="bg-surface pt-6 sm:pt-10 md:pt-12 pb-6 sm:pb-8 px-4 sm:px-6 md:px-8 rounded-lg border border-ink-200 mb-8 shadow-inner overflow-hidden">
            {/* =========================================================
                TIER 1 — BOTTOM LINE UP FRONT
                ========================================================= */}
            {/* Header / Identity */}
            <div className="pdf-section mb-8 sm:mb-10 px-0 sm:px-4 md:px-7">
              <div className="flex flex-col sm:flex-row items-end sm:items-center justify-between border-b-2 border-ink pb-8 gap-6">
                <div className="flex items-center gap-6 w-full sm:w-auto">
                  <div className="w-16 h-16 bg-oxblood rounded-lg flex items-center justify-center shadow-overlay shrink-0">
                    <ShieldCheck className="w-9 h-9 text-paper" />
                  </div>
                  <div>
                    <div className="text-4xl font-serif font-medium italic tracking-tighter text-ink leading-none mb-2">
                      Oracle Insight
                    </div>
                    <div className="text-[11px] font-medium uppercase tracking-[0.3em] text-ink-500">
                      The Intelligent Investor's Operating System
                    </div>
                  </div>
                </div>
                <div className="text-right shrink-0 border-l border-ink-200 pl-10 ml-10 hidden sm:block">
                  <div className="text-[8px] font-medium uppercase tracking-[0.6em] text-ink-500 mb-1.5">
                    Confidential Asset
                  </div>
                  <div className="text-[8px] font-medium uppercase tracking-[0.6em] text-ink-500 mb-1.5">
                    Institutional Grade Analysis
                  </div>
                  <div className="flex items-center gap-2 mt-3 justify-end">
                    <div className="px-2 py-1 border border-[var(--color-danger-100)] text-danger-600 text-[7px] font-medium uppercase tracking-widest rounded-md bg-[var(--color-danger-50)]">
                      Restricted
                    </div>
                    <div className="px-2 py-1 border border-ink-200 text-ink-500 text-[7px] font-medium uppercase tracking-widest rounded-md bg-surface-2">
                      Proprietary
                    </div>
                  </div>
                  <div className="text-[7px] font-mono text-ink-300 mt-3 tracking-widest">
                    DOC-ID: {analysis.ticker}-
                    {new Date().getTime().toString(36).toUpperCase()}
                  </div>
                </div>
              </div>
            </div>

            <div className="pdf-section px-0 sm:px-4 md:px-7 mb-10 sm:mb-12">
              <div className="flex justify-between items-start border-b border-ink-200 pb-10">
                <div className="space-y-2">
                  <h4 className="text-3xl font-medium uppercase tracking-[0.15em] text-ink leading-none">
                    {analysis.ticker} Analysis
                  </h4>
                  <div className="flex flex-wrap items-center gap-3">
                    <p className="text-[11px] font-medium uppercase tracking-[0.3em] text-ink-500 flex items-center gap-3">
                      <span>{analysis.name}</span>
                      <span className="w-1 h-1 bg-ink-300 rounded-full" />
                      <span>
                        {formatCurrencyValue(analysis.lastYearRevenue)} Revenue
                      </span>
                    </p>
                    {analysis.entityType && (
                      <span className="text-[10px] font-medium uppercase tracking-widest text-ink-500 border border-ink-200 rounded-md px-3 py-1">
                        {analysis.entityType}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1.5 text-right mt-1 sm:mt-0">
                  <div className="flex items-baseline gap-2">
                    <div className="text-3xl font-mono font-medium tracking-tighter text-ink leading-none">
                      {analysis.currentPrice
                        ? formatNumber(analysis.currentPrice, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        : "N/A"}
                    </div>
                    <div className="text-xs font-medium text-ink-500 uppercase tracking-widest">
                      USD
                    </div>
                  </div>
                  {analysis.priceChange !== undefined &&
                    analysis.priceChangePercent !== undefined && (
                      <div
                        className={cn(
                          "flex items-center gap-1.5 text-[11px] font-medium tracking-wide",
                          analysis.priceChange >= 0
                            ? "text-success-600"
                            : "text-danger-600",
                        )}
                      >
                        <span>
                          {analysis.priceChange >= 0 ? "+" : ""}
                          {safeToFixed(analysis.priceChange, 2)}
                        </span>
                        <span>
                          ({analysis.priceChangePercent >= 0 ? "+" : ""}
                          {safeToFixed(analysis.priceChangePercent, 2)}%)
                        </span>
                      </div>
                    )}
                  <div className="flex items-center gap-2 mt-2">
                    <div className="text-[8px] font-medium uppercase tracking-[0.2em] text-ink-500">
                      Analysis Date:
                    </div>
                    <div className="text-[10px] font-medium text-ink tracking-tight">
                      {new Date().toLocaleDateString(undefined, {
                        month: "long",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Decision Banner */}
            <div className="mb-10">
              <DarkEmphasisCard
                icon={ShieldCheck}
                title="Final Verdict & Position Sizing"
              >
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                  <div className="space-y-8">
                    <div>
                      <div className="text-[10px] font-medium uppercase tracking-[0.4em] text-ink-500 mb-4">
                        Directional Call
                      </div>
                      <div
                        className={cn(
                          "text-[clamp(2.5rem,4vw,3.75rem)] font-serif font-medium italic tracking-tighter leading-none break-words",
                          dcfModel.decision.action === "BUY" ||
                            dcfModel.decision.action === "ACCUMULATE" ||
                            dcfModel.decision.action === "FAT PITCH"
                            ? "text-success-600"
                            : dcfModel.decision.action === "CORE HOLD" ||
                                dcfModel.decision.action === "HOLD" ||
                                dcfModel.decision.action === "WATCH" ||
                                dcfModel.decision.action === "PASS"
                              ? "text-warning-600"
                              : dcfModel.decision.action === "AVOID"
                                ? "text-danger-600"
                                : "text-ink",
                        )}
                      >
                        {dcfModel.decision.action}
                        {dcfModel.decision.qualifier}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] font-medium uppercase tracking-[0.4em] text-ink-500 mb-4">
                        Position Sizing
                      </div>
                      <div className="text-3xl font-mono font-medium text-ink uppercase">
                        {dcfModel.decision.action === "CORE HOLD" ||
                        dcfModel.decision.action === "HOLD"
                          ? "CORE HOLD"
                          : dcfModel.decision.action === "PASS" ||
                              dcfModel.decision.action === "AVOID" ||
                              dcfModel.decision.action === "WATCH"
                            ? dcfModel.decision.action === "WATCH"
                              ? "WAIT / WATCH"
                              : dcfModel.decision.action
                            : analysis.capitalAllocationDecision}
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col justify-center">
                    <div className="p-8 bg-surface-2 rounded-lg border border-ink-200">
                      <div className="text-[10px] font-medium uppercase tracking-[0.4em] text-ink-500 mb-6">
                        Conviction Rationale
                      </div>
                      <p className="text-xl text-ink leading-relaxed mb-4">
                        {analysis.allocationGuidance}
                      </p>
                      {analysis.cycleSensitivityPenalty && (
                        <div className="pt-4 border-t border-ink-200">
                          <div className="text-[9px] font-medium uppercase tracking-[0.4em] text-warning-600 mb-2 flex items-center gap-2">
                            <RefreshCw className="w-3 h-3" />
                            Cycle Sensitivity Penalty
                          </div>
                          <p className="text-sm text-warning-700">
                            {analysis.cycleSensitivityPenalty}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </DarkEmphasisCard>
            </div>

            {/* Spin-off Notice */}
            {analysis.isType5DataContaminated && (
              <div className="pdf-section mb-10 p-6 bg-warning-50 border-l-4 border-warning-600 rounded-r-2xl">
                <div className="flex items-center gap-3 mb-3">
                  <AlertTriangle className="w-5 h-5 text-warning-600 shrink-0" />
                  <span className="text-[10px] font-medium uppercase tracking-[0.25em] text-warning-700">
                    Valuation Not Available — Recent Spin-off
                  </span>
                </div>
                <p className="text-sm text-warning-700 leading-relaxed mb-2">
                  This company was recently separated from its parent company.
                  The financial data available reflects the parent's
                  consolidated historical operations, not this entity's
                  standalone performance. Oracle's intrinsic value framework
                  requires a minimum of 2–3 years of audited standalone annual
                  filings to anchor a reliable discounted cash flow model.
                </p>
                <p className="text-sm text-warning-700 leading-relaxed">
                  The <strong>PASS verdict is deliberate</strong>. This is not a
                  system limitation — it reflects the same discipline Warren
                  Buffett applies: he will not buy a business he cannot value,
                  and he cannot value a business without a verifiable earnings
                  track record. The qualitative analysis that follows reflects
                  Oracle's assessment of the business model, competitive
                  position, and structural risks based on available public
                  information.
                </p>
              </div>
            )}

            {/* Executive Thesis */}
            <div className="pdf-section oi-card p-6 md:p-7 mb-10">
              <SectionHeader
                icon={Briefcase}
                title="Executive Thesis"
                className="mb-8"
              />
              {typeof analysis.analystSearchConfidenceScore === "number" &&
                analysis.analystSearchConfidenceScore < 0.5 && (
                  <div className="flex items-start gap-3 mb-6 p-3 bg-surface-2 border border-ink-200 rounded-md">
                    <Info className="w-4 h-4 text-ink-500 shrink-0 mt-0.5" />
                    <p className="text-[10px] font-medium text-ink-500 uppercase tracking-wider leading-relaxed">
                      Market Intelligence Limited — The Analyst retrieved
                      limited current data for this company. Qualitative context
                      is based primarily on financial filings rather than live
                      market intelligence.
                    </p>
                  </div>
                )}
              <div className="text-2xl leading-[1.8] text-ink font-medium markdown-body">
                <ReactMarkdown>{analysis.summary}</ReactMarkdown>
              </div>
            </div>

            {/* Key Metrics at a Glance */}
            <div className="mb-10">
              <MetricStrip
                metrics={[
                  {
                    label: "Current Price",
                    value: analysis.currentPrice
                      ? "$" +
                        formatNumber(analysis.currentPrice, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })
                      : "N/A",
                  },
                  {
                    label: "Intrinsic Value",
                    value: (dcfModel?.isEstimable && !analysis?.isUnresolvableValuation)
                      ? (
                          <div className="flex flex-col gap-[3px] w-full text-[11px] leading-tight mt-1">
                            <div className="flex justify-between items-center text-success-600 border-b border-ink-100/50 pb-[2px]">
                              <span className="text-[8px] font-sans tracking-widest uppercase">Bull</span>
                              <span>${formatNumber(dcfModel.scenarios.bull, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                            <div className="flex justify-between items-center text-ink font-semibold border-b border-ink-100/50 pb-[2px]">
                              <span className="text-[8px] font-sans tracking-widest uppercase">Base</span>
                              <span>${formatNumber(dcfModel.scenarios.base, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                            <div className="flex justify-between items-center text-danger-600">
                              <span className="text-[8px] font-sans tracking-widest uppercase">Bear</span>
                              <span>${formatNumber(dcfModel.scenarios.bear, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                            {analysis.sensitivities && (
                              <div className="mt-1 pt-1 border-t border-ink-200">
                                <div className="flex justify-between items-center text-ink-600">
                                  <span className="text-[7px] font-sans tracking-widest uppercase opacity-80">± 1% WACC →</span>
                                  <span className="text-[8px] oi-figure">∓ ${Math.abs(analysis.sensitivities.ivDeltaPerWaccPlus1).toFixed(2)} / sh</span>
                                </div>
                                <div className="flex justify-between items-center text-ink-600">
                                  <span className="text-[7px] font-sans tracking-widest uppercase opacity-80">± 2% growth →</span>
                                  <span className="text-[8px] oi-figure">± ${Math.abs(analysis.sensitivities.ivDeltaPerGrowthPlus2).toFixed(2)} / sh</span>
                                </div>
                              </div>
                            )}
                          </div>
                      )
                      : "N/A",
                  },
                  {
                    label: "Premium",
                    // !analysis.isType5DataContaminated is enforced via isEstimable
                    value:
                      (dcfModel?.isEstimable && !analysis?.isUnresolvableValuation) && analysis.currentPrice && dcfModel?.intrinsicValuePerShare
                        ? safeToFixed(
                            (analysis.currentPrice /
                              dcfModel.intrinsicValuePerShare -
                              1) *
                              100,
                            2,
                          ) + "%"
                        : "N/A",
                    badge: true,
                  },
                  {
                    label: "Base IRR",
                    // Mirrors the IV/Premium tiles: isEstimable is false on
                    // contaminated Type-5 names, where realisticIrr would be
                    // an unchecked LLM value (the IRR invariant is skipped).
                    value: (dcfModel?.isEstimable && !analysis?.isUnresolvableValuation)
                      ? analysis.expectedReturnMath?.realisticIrr || "N/A"
                      : "N/A",
                  },
                  ...(analysis.unitEconomics
                    ?.slice(0, 2)
                    .map((u) => ({ label: u.label, value: u.value })) || []),
                ]}
              />
            </div>

            {/* Convexity panel */}
            {showAsymmetry && (
              <div className="pdf-section mb-10 bg-surface border border-ink-200 rounded-lg p-6 md:p-8 space-y-4">
                <SectionHeader icon={Scale} title="Convexity & Asymmetry" />
                <div className="flex flex-col md:flex-row gap-6 mt-6 justify-between items-center">
                  <div className="flex flex-col w-full md:w-5/12 pb-4 md:pb-0 border-b md:border-b-0 md:border-r border-ink-100/50 pr-4">
                    <div className="flex justify-between items-center text-success-600 mb-1">
                      <span className="text-[10px] font-medium uppercase tracking-widest">Upside Case</span>
                      <span className="text-2xl oi-figure">{upsidePct > 0 ? "+" : ""}{(upsidePct * 100).toFixed(1)}%</span>
                    </div>
                    <span className="text-[9px] text-success-600/70">Scenario weight: {bullScenarioWeight} (model judgment)</span>
                  </div>
                  
                  <div className="flex flex-col w-full md:w-5/12 pb-4 md:pb-0 border-b md:border-b-0 md:border-r border-ink-100/50 px-4">
                    <div className="flex justify-between items-center text-danger-600 mb-1">
                      <span className="text-[10px] font-medium uppercase tracking-widest">Downside Case</span>
                      <span className="text-2xl oi-figure">{downsidePct > 0 ? "+" : ""}{(downsidePct * 100).toFixed(1)}%</span>
                    </div>
                    <span className="text-[9px] text-danger-600/70">Scenario weight: {bearScenarioWeight} (model judgment)</span>
                  </div>

                  <div className="flex flex-col w-full md:w-2/12 pl-4 justify-center">
                    {asymmetryRatio !== null ? (
                      <>
                        <span className="text-[10px] font-medium uppercase tracking-widest text-ink-600 mb-1">Asymmetry</span>
                        <span className="text-lg oi-figure text-ink">~ {asymmetryRatio.toFixed(1)}x</span>
                      </>
                    ) : (
                       <span className="text-[10px] font-medium uppercase tracking-widest text-ink-400">Neutral</span>
                    )}
                  </div>
                </div>
                <div className="text-[8px] text-ink-400 italic mt-4 pt-4 border-t border-ink-100">
                  Note: Scenario weights are qualitative AI confidence levels, not calibrated numeric probabilities.
                </div>
              </div>
            )}

            {/* The Killer Insight */}
            {analysis.killerInsight && (
              <div className="mb-10">
                <Callout
                  icon={Target}
                  title="The Killer Insight (The Lynchpin)"
                  content={analysis.killerInsight}
                />
              </div>
            )}

            {/* =========================================================
                TIER 2 — THE SUPPORTING CASE
                ========================================================= */}
            {/* 1. Valuation Section (Consolidated) */}
            <div className="mb-10 border-t border-ink-200 pt-10">
              <div className="pdf-section pdf-keep-with-next mb-8">
                <h4 className="text-2xl font-serif font-medium italic text-ink">
                  Valuation Framework
                </h4>
              </div>
              <div className="pdf-section oi-card p-6 md:p-7 overflow-hidden mb-10">
                <SectionHeader
                  icon={Layers}
                  title="10-Year Cash Flow Projection"
                />

                {(analysis.isUnresolvableValuation || analysis.isType5DataContaminated) && (
                  <div className="p-6 rounded-md bg-surface-2 border border-ink-200 text-sm text-ink-600 leading-relaxed">
                    <strong>Projection unavailable.</strong>{" "}
                    {analysis.isType5DataContaminated
                      ? "This recent spin-off lacks standalone financial history (databases backfill the former parent's results onto this ticker), so no reliable owner-earnings projection or intrinsic value can be produced."
                      : "The deterministic engine marked this valuation unresolvable (structural data anomaly or missing valuation inputs), so no reliable owner-earnings projection or intrinsic value can be produced."}{" "}
                    See the Deep Valuation Insights note above.
                  </div>
                )}

                {engineBase?.cashFlowBasis === "FCFF" && (
                  <div className="mb-6 p-4 bg-surface-2 border border-ink-200 rounded-lg flex items-start gap-3">
                    <div className="text-ink-500 text-lg">ℹ️</div>
                    <div className="text-sm text-ink-600 leading-relaxed">
                      <strong>
                        Unlevered DCF (FCFF) — Net debt deducted at bridge.
                      </strong>
                      <br />
                      Interest expense excluded from projected cash flows to
                      avoid double-counting.
                    </div>
                  </div>
                )}

                {/* RI‑3: deterministic residual‑income walk for RI‑applied Type‑4.
                    Replaces the discarded live‑DCF projection so the panel is
                    additive (tangible book + ΣPV(RI) + PV continuing = RI IV). */}
                {isRiWalk && !analysis.isUnresolvableValuation && !analysis.isType5DataContaminated && (
                  <>
                    <div className="overflow-x-auto pb-4 no-scrollbar">
                      <table className="w-full border-separate border-spacing-x-2 md:border-spacing-x-6">
                        <thead>
                          <tr>
                            <th className="pb-8 text-[8px] md:text-[9px] font-medium uppercase tracking-[0.2em] text-ink-500 text-left w-24 md:w-40">
                              Metric (per share)
                            </th>
                            {riWalk.map((p) => (
                              <th
                                key={p.year}
                                className="pb-8 text-[8px] md:text-[9px] font-medium uppercase tracking-[0.2em] text-ink-500 text-right"
                              >
                                <div className="bg-surface-2 rounded-lg md:rounded-md py-1 px-2 md:py-2 md:px-3 text-ink">
                                  Yr {p.year}
                                </div>
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-black/5">
                          <tr>
                            <td className="py-4 md:py-6 text-[8px] md:text-[9px] font-medium uppercase tracking-[0.2em] text-ink-500">
                              Begin. Tangible Book
                            </td>
                            {riWalk.map((p) => (
                              <td
                                key={p.year}
                                className="py-4 md:py-6 text-[10px] md:text-xs font-mono font-medium text-right text-ink"
                              >
                                {`$${(p.beginningBook || 0).toFixed(2)}`}
                              </td>
                            ))}
                          </tr>
                          <tr>
                            <td className="py-4 md:py-6 text-[8px] md:text-[9px] font-medium uppercase tracking-[0.2em] text-ink-500">
                              ROTCE
                            </td>
                            {riWalk.map((p) => (
                              <td
                                key={p.year}
                                className="py-4 md:py-6 text-[10px] md:text-xs font-mono text-right text-ink-500"
                              >
                                {`${((p.rotce || 0) * 100).toFixed(1)}%`}
                              </td>
                            ))}
                          </tr>
                          <tr>
                            <td className="py-4 md:py-6 text-[8px] md:text-[9px] font-medium uppercase tracking-[0.2em] text-ink-500">
                              Residual Income
                            </td>
                            {riWalk.map((p) => (
                              <td
                                key={p.year}
                                className="py-4 md:py-6 text-[10px] md:text-xs font-mono font-medium text-right text-ink"
                              >
                                {`$${(p.residualIncome || 0).toFixed(2)}`}
                              </td>
                            ))}
                          </tr>
                          <tr>
                            <td className="py-4 md:py-6 text-[8px] md:text-[9px] font-medium uppercase tracking-[0.2em] text-ink-500">
                              PV of Residual Income
                            </td>
                            {riWalk.map((p) => (
                              <td
                                key={p.year}
                                className="py-4 md:py-6 text-[10px] md:text-xs font-mono text-right text-ink-500"
                              >
                                {`$${(p.pvResidualIncome || 0).toFixed(2)}`}
                              </td>
                            ))}
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {/* [SPEC-9] Name the book-compounding rate the walk above actually
                        uses. Without this the only labeled "growth" in the memo is the
                        7.5% scenario input, which reads as though the IV were built on
                        it. Deterministic (observability.riImpliedBookGrowthPct, the
                        CAGR of the walk's own beginningBook series); rendered only when
                        the engine reports it. */}
                    {typeof riImpliedBookGrowthPct === "number" && (
                      <p className="mt-4 text-[10px] text-ink-400 italic">
                        Tangible book compounds at ~{riImpliedBookGrowthPct.toFixed(1)}%/yr
                        (ROTCE × retention) across this walk — this is the residual-income
                        book path, not the scenario growth input.
                      </p>
                    )}

                    <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 p-6 md:p-8 bg-surface-2 rounded-lg border border-ink-200">
                      <div className="space-y-1">
                        <div className="text-[7px] md:text-[8px] font-medium uppercase tracking-[0.2em] text-ink-500">
                          Tangible Book Anchor / sh
                        </div>
                        <div className="text-sm md:text-lg font-mono font-medium tracking-tighter text-ink">
                          {`$${(riAnchorPerShare || 0).toFixed(2)}`}
                        </div>
                      </div>
                      <div className="space-y-1">
                        <div className="text-[7px] md:text-[8px] font-medium uppercase tracking-[0.2em] text-ink-500">
                          Σ PV Residual Income / sh
                        </div>
                        <div className="text-sm md:text-lg font-mono font-medium tracking-tighter text-ink">
                          {`$${(riSumPvPerShare || 0).toFixed(2)}`}
                        </div>
                      </div>
                      <div className="space-y-1">
                        <div className="text-[7px] md:text-[8px] font-medium uppercase tracking-[0.2em] text-ink-500">
                          PV Continuing Value (ω-faded) / sh
                        </div>
                        <div className="text-sm md:text-lg font-mono font-medium tracking-tighter text-ink">
                          {`$${(riPvContinuingPerShare || 0).toFixed(2)}`}
                        </div>
                      </div>
                      <div className="space-y-1">
                        <div className="text-[7px] md:text-[8px] font-medium uppercase tracking-[0.2em] text-ink-500">
                          RI Intrinsic Value / sh
                        </div>
                        <div className="text-sm md:text-lg font-mono font-medium tracking-tighter text-ink">
                          {`$${(riValuePerShare || 0).toFixed(2)}`}
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 text-[10px] md:text-[11px] leading-relaxed text-ink-500 italic">
                      {`Deterministic residual-income walk (Type 4). Value = tangible book + present value of returns earned above the cost of equity (Ke ${riKeUsedPct != null ? riKeUsedPct.toFixed(2) + "%" : "n/a"}), faded by the Ohlson persistence factor ω=${riOmega != null ? Number(riOmega).toFixed(2) : "n/a"}. Tangible Book Anchor + Σ PV(Residual Income) + PV(Continuing Value) = RI Intrinsic Value.`}
                      {riClearedUnresolvable
                        ? " Owner-earnings normalization was degenerate for this entity; the residual-income model is owner-earnings-independent and carries the valuation, with residual income reflecting ROTCE relative to the cost of equity."
                        : ""}
                    </div>
                  </>
                )}

                {!isRiWalk && !analysis.isUnresolvableValuation && !analysis.isType5DataContaminated && (
                <div className="overflow-x-auto pb-4 no-scrollbar">
                  <table className="w-full border-separate border-spacing-x-2 md:border-spacing-x-6">
                    <thead>
                      <tr>
                        <th className="pb-8 text-[8px] md:text-[9px] font-medium uppercase tracking-[0.2em] text-ink-500 text-left w-24 md:w-40">
                          Metric
                        </th>
                        {(engineProjections.length > 0
                          ? engineProjections
                          : dcfModel.projections
                        ).map((p: any) => (
                          <th
                            key={p.year}
                            className="pb-8 text-[8px] md:text-[9px] font-medium uppercase tracking-[0.2em] text-ink-500 text-right"
                          >
                            <div className="bg-surface-2 rounded-lg md:rounded-md py-1 px-2 md:py-2 md:px-3 text-ink">
                              Yr {p.year}
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-black/5">
                      <tr>
                        <td className="py-4 md:py-6 text-[8px] md:text-[9px] font-medium uppercase tracking-[0.2em] text-ink-500">
                          Proj. Owner Earnings
                        </td>
                        {(engineProjections.length > 0
                          ? engineProjections
                          : dcfModel.projections
                        ).map((p: any) => (
                          <td
                            key={p.year}
                            className="py-4 md:py-6 text-[10px] md:text-xs font-mono font-medium text-right text-ink"
                          >
                            {formatProjectionValue((engineProjections.length > 0 ? p.oe : p.fcf) || 0)}
                          </td>
                        ))}
                      </tr>
                      <tr>
                        <td className="py-4 md:py-6 text-[8px] md:text-[9px] font-medium uppercase tracking-[0.2em] text-ink-500">
                          PV of Owner Earnings
                        </td>
                        {(engineProjections.length > 0
                          ? engineProjections
                          : dcfModel.projections
                        ).map((p: any) => (
                          <td
                            key={p.year}
                            className="py-4 md:py-6 text-[10px] md:text-xs font-mono text-right text-ink-500"
                          >
                            {formatProjectionValue((engineProjections.length > 0 ? p.pv : p.pv) || 0)}
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>
                )}

                {!isRiWalk && !analysis.isUnresolvableValuation && !analysis.isType5DataContaminated && (
                <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 p-6 md:p-8 bg-surface-2 rounded-lg border border-ink-200">
                  <div className="space-y-1">
                    <div className="text-[7px] md:text-[8px] font-medium uppercase tracking-[0.2em] text-ink-500">
                      Sum of PVs (1-10)
                    </div>
                    <div className="text-sm md:text-lg font-mono font-medium tracking-tighter text-ink">
                      {`$${(((engineSumOfPVs !== null ? engineSumOfPVs : dcfModel.totalPv) || 0) / 1000).toFixed(1)}B`}
                    </div>
                  </div>
                  <div className="space-y-1">
                    <div className="text-[7px] md:text-[8px] font-medium uppercase tracking-[0.2em] text-ink-500">
                      PV of Terminal Value
                    </div>
                    <div className="text-sm md:text-lg font-mono font-medium tracking-tighter text-ink">
                      {`$${(((enginePvTerminal !== null ? enginePvTerminal : dcfModel.pvTerminalValue) || 0) / 1000).toFixed(1)}B`}
                    </div>
                  </div>
                  <div className="space-y-1">
                    <div className="text-[7px] md:text-[8px] font-medium uppercase tracking-[0.2em] text-ink-500">
                      {/* (Tuning v2.94 F-8) On the FCFF bridge the value renders signed
                          (−$390M = amount deducted); label it explicitly so "Net Debt −$390M"
                          cannot be misread as net cash. */}
                      {analysis.netDebt <= 0
                        ? "Net Cash"
                        : engineBase?.cashFlowBasis === "FCFF"
                          ? "Net Debt (Deducted)"
                          : "Net Debt"}
                    </div>
                    <div
                      className={cn(
                        "text-sm md:text-lg font-mono font-medium tracking-tighter",
                        analysis.entityType === "Financial" ||
                          analysis.entityType === "Type 4" ||
                          (engineBase?.cashFlowBasis === "OwnerEarnings" &&
                            analysis.netDebt > 0)
                          ? "text-ink-500 italic"
                          : analysis.netDebt <= 0
                            ? "text-success-600"
                            : "text-danger-600",
                      )}
                    >
                      {(() => {
                        if (
                          analysis.entityType === "Financial" ||
                          analysis.entityType === "Type 4"
                        ) {
                          return "N/A — Type 4";
                        }
                        if (analysis.netDebt <= 0) {
                          // (S4) The engine applies a cash-accessibility haircut
                          // (repatriation/friction) INSIDE the DCF: Total Equity
                          // Value uses adjustedNetCash, not gross net cash. Render
                          // the APPLIED value so Sum PV + PV Terminal + Net Cash
                          // foots exactly to Total Equity. Fall back to gross only
                          // if the engine value is unavailable.
                          const appliedNetCash =
                            typeof engineBase?.adjustedNetCash === "number"
                              ? engineBase.adjustedNetCash
                              : Math.abs(analysis.netDebt);
                          return `+${formatCurrencyValue(appliedNetCash)}`;
                        }
                        if (
                          engineBase &&
                          engineBase.cashFlowBasis === "OwnerEarnings"
                        ) {
                          return "N/A (Included in CF)";
                        }
                        return `-${formatCurrencyValue(analysis.netDebt)}`;
                      })()}
                    </div>
                    {/* (S4) Disclosure: when the engine applied a cash haircut,
                        show gross net cash and the derived deduction (gross −
                        applied) so the page-2 bridge stays consistent with the
                        page-3 haircut narrative. Amount is engine-derived — no
                        hardcoded percentage. */}
                    {analysis.netDebt <= 0 &&
                      typeof engineBase?.adjustedNetCash === "number" &&
                      Math.abs(analysis.netDebt) - engineBase.adjustedNetCash > 1 && (
                        <div className="text-[7px] md:text-[8px] text-ink-400 font-mono not-italic leading-tight">
                          gross {formatCurrencyValue(Math.abs(analysis.netDebt))} · −
                          {formatCurrencyValue(
                            Math.abs(analysis.netDebt) - engineBase.adjustedNetCash,
                          )}{" "}
                          haircut
                        </div>
                      )}
                  </div>
                  <div className="space-y-1">
                    <div className="text-[7px] md:text-[8px] font-medium uppercase tracking-[0.2em] text-ink-500">
                      Total Equity Value
                    </div>
                    <div className="text-sm md:text-lg font-mono font-medium tracking-tighter text-ink">
                      {`$${(
                        ((engineTotalEquity !== null
                          ? engineTotalEquity
                          : analysis.entityType === "Financial" ||
                              analysis.entityType === "Type 4"
                            ? analysis.oracleFramework?.oracleIntrinsicValue
                              ? analysis.oracleFramework.oracleIntrinsicValue *
                                (analysis.sharesOutstanding || 1)
                              : dcfModel.enterpriseValue
                            : dcfModel.enterpriseValue - analysis.netDebt) ||
                          0) / 1000
                      ).toFixed(1)}B`}
                    </div>
                  </div>
                </div>
                )}

                {analysis.valuationBridge && !analysis.isUnresolvableValuation && !analysis.isType5DataContaminated && (
                  <div className="mt-8 pt-8 border-t border-ink-200">
                    <h6 className="text-[10px] font-medium uppercase tracking-[0.2em] text-ink-600 mb-6">
                      Valuation Bridge (Per Share Reconciliation)
                    </h6>
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-6">
                      {[
                        {
                          label: "EV",
                          value: analysis.valuationBridge.enterpriseValue,
                        },
                        {
                          label:
                            analysis.netDebt <= 0 ? "Net Cash" : "Net Debt",
                          value: analysis.valuationBridge.netDebt,
                          negative: analysis.netDebt > 0,
                        },
                        {
                          label: "Equity Val.",
                          value: analysis.valuationBridge.equityValue,
                        },
                        {
                          label: "Goodwill",
                          value: analysis.valuationBridge.goodwill || "N/A",
                        },
                        {
                          label: "Tangible BV",
                          value:
                            analysis.valuationBridge.tangibleBookValue || "N/A",
                          negative:
                            analysis.valuationBridge.tangibleBookValue?.startsWith(
                              "-",
                            ),
                        },
                        {
                          label: "Shares",
                          value: analysis.valuationBridge.sharesOutstanding,
                        },
                        {
                          label: "IV/Share",
                          value:
                            analysis.valuationBridge.intrinsicValuePerShare,
                          bold: true,
                        },
                      ].map((item, idx) => (
                        <div key={idx} className="space-y-1">
                          <div className="text-[8px] font-medium uppercase tracking-widest text-ink-500">
                            {item.label}
                          </div>
                          <div
                            className={cn(
                              "text-xs font-mono font-medium",
                              item.negative
                                ? "text-danger-600"
                                : item.bold
                                  ? "text-oxblood"
                                  : "text-ink",
                            )}
                          >
                            {item.negative ? "-" : ""}
                            {item.value}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-6 flex gap-6 pt-4 border-t border-ink-200">
                      <div className="flex items-center gap-2">
                        <span className="text-[8px] font-medium uppercase tracking-widest text-ink-500">
                          Discount Rate:
                        </span>
                        <span className="text-[10px] font-mono font-medium text-ink">
                          {analysis.valuationBridge.discountRate}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[8px] font-medium uppercase tracking-widest text-ink-500">
                          Terminal Growth:
                        </span>
                        <span className="text-[10px] font-mono font-medium text-ink">
                          {analysis.valuationBridge.terminalGrowthRate}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {analysis.segmentAnalysis &&
                analysis.segmentAnalysis.length > 0 && (
                  <div className="pdf-section mb-10 oi-card p-7">
                    <SectionHeader
                      icon={Layers}
                      title="Segment-Level Valuation (Sum-of-the-Parts)"
                    />
                    <div className="overflow-x-auto">
                      <table className="w-full text-left border-collapse">
                        <thead>
                          <tr className="border-b-2 border-ink text-[10px] font-medium uppercase tracking-widest text-ink-500">
                            <th className="py-4 pr-4">Segment</th>
                            <th className="py-4 px-4">Revenue</th>
                            <th className="py-4 px-4">Margin</th>
                            <th className="py-4 px-4">Multiple</th>
                            <th className="py-4 pl-4 text-right">
                              Implied Value
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-ink-200">
                          {analysis.segmentAnalysis.map((segment, idx) => (
                            <tr
                              key={idx}
                              className={cn(
                                segment.isPrimaryEdge && "bg-warning-50",
                              )}
                            >
                              <td className="py-6 pr-4">
                                <div className="text-sm font-medium text-ink">
                                  {segment.name}
                                </div>
                              </td>
                              <td className="py-6 px-4 text-sm font-mono">
                                {segment.revenue}
                              </td>
                              <td className="py-6 px-4 text-sm font-mono">
                                {segment.margin}
                              </td>
                              <td className="py-6 px-4 text-sm font-mono">
                                {segment.compMultiple}
                              </td>
                              <td className="py-6 pl-4 text-right">
                                <div className="text-sm font-mono font-medium text-ink">
                                  {segment.impliedValue}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

              {analysis.expectedReturnMath && !analysis.isType5DataContaminated && !analysis.isUnresolvableValuation && (
                <div className="pdf-section oi-card p-7 mb-10">
                  <SectionHeader
                    icon={Calculator}
                    title="Expected Return Math (IRR Analysis)"
                  />
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mb-10">
                    <div className="p-6 rounded-md bg-surface-2 border border-ink-200">
                      <div className="text-[10px] font-medium uppercase tracking-widest text-ink-500 mb-3">
                        Implied Growth
                      </div>
                      <div className="text-base text-ink leading-tight font-medium font-mono">
                        {analysis.expectedReturnMath.impliedGrowth}
                      </div>
                    </div>
                    <div className="p-6 rounded-md bg-surface-2 border border-ink-200">
                      <div className="text-[10px] font-medium uppercase tracking-widest text-ink-500 mb-3">
                        Required IRR
                      </div>
                      <div className="text-base text-ink leading-tight font-medium font-mono">
                        {analysis.expectedReturnMath.requiredIrr}
                      </div>
                    </div>
                    <div className="p-6 rounded-md bg-oxblood text-paper">
                      <div className="text-[10px] font-medium uppercase tracking-widest text-paper/60 mb-3">
                        Realistic IRR
                      </div>
                      <div className="text-base text-paper leading-tight font-medium font-mono">
                        {analysis.expectedReturnMath.realisticIrr}
                      </div>
                    </div>
                  </div>
                  <div className="pt-8 border-t border-ink-200">
                    <div className="text-[9px] font-medium uppercase tracking-widest text-ink-500 mb-3">
                      Valuation Rationale
                    </div>
                    <p className="text-base text-ink leading-relaxed">
                      {stripValuationGap(analysis.expectedReturnMath.rationale)}
                    </p>
                  </div>
                </div>
              )}

              {analysis.valuationScenarios &&
                analysis.valuationScenarios.length > 0 &&
                !analysis.isType5DataContaminated &&
                !analysis.isUnresolvableValuation && (
                  <div className="pdf-section mb-10 oi-card p-7">
                    <SectionHeader
                      icon={Table}
                      title="Valuation Scenarios (Bear / Base / Bull)"
                    />
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                      {analysis.valuationScenarios.map((scenario, idx) => (
                        <div
                          key={idx}
                          className={cn(
                            "p-8 rounded-lg border transition-all duration-300",
                            scenario.scenario === "Bear"
                              ? "bg-danger-50 border-danger-100"
                              : scenario.scenario === "Base"
                                ? "bg-surface-2 border-ink-200"
                                : "bg-success-50 border-success-100",
                          )}
                        >
                          <div className="flex items-center justify-between mb-6">
                            <div
                              className={cn(
                                "text-[10px] font-medium uppercase tracking-[0.2em]",
                                scenario.scenario === "Bear"
                                  ? "text-danger-600"
                                  : scenario.scenario === "Base"
                                    ? "text-ink"
                                    : "text-success-600",
                              )}
                            >
                              {scenario.scenario} Case
                            </div>
                            <span className="px-2 py-1 bg-surface-2/50 rounded text-[8px] font-medium uppercase tracking-widest text-ink-500">
                              {scenario.probability} Prob.
                            </span>
                          </div>
                          <div className="space-y-4">
                            <div>
                              <div className="text-[9px] font-medium uppercase tracking-widest text-ink-500 mb-1">
                                Growth Assumption
                              </div>
                              <div className="text-xl font-mono font-medium text-ink">
                                {scenario.growth}
                              </div>
                              {/* [SPEC-9] Name this figure for what it is. It is the
                                  scenario GROWTH INPUT, never the rate the RI walk
                                  compounds tangible book at — the memo used the bare word
                                  "growth" for both, so a 7.5% input read as though the IV
                                  were built on it. The contrast clause is RI-only: a
                                  standard DCF has no book-compounding rate to confuse it
                                  with. */}
                              <div className="mt-1 text-[9px] text-ink-400 italic">
                                Scenario input
                                {isRiWalk ? " — not the book-compounding rate" : ""}
                              </div>
                            </div>
                            <div>
                              <div className="text-[9px] font-medium uppercase tracking-widest text-ink-500 mb-1">
                                Implied IRR
                              </div>
                              <div className="text-xl font-mono font-medium text-ink">
                                {scenario.irr}
                              </div>
                            </div>
                            {scenario.drivers && (
                              <div className="pt-4 border-t border-ink-200">
                                <div className="text-[9px] font-medium uppercase tracking-widest text-ink-500 mb-2">
                                  Key Drivers
                                </div>
                                <p className="text-[10px] text-ink-600 leading-relaxed">
                                  {scenario.drivers}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              <Callout
                icon={Scale}
                title="Valuation Conclusion"
                className="mb-10"
                content={stripValuationGap(analysis.valuationRationale)}
                action={
                  analysis.entryStrategyMode ? (
                    <div className="px-3 py-1.5 bg-oxblood-50 border border-oxblood/20 rounded-md text-[9px] font-medium uppercase tracking-widest text-oxblood flex items-center gap-2">
                      <Target className="w-3 h-3" />
                      {analysis.entryStrategyMode}
                    </div>
                  ) : undefined
                }
              />

              {analysis.buyZoneFramework &&
                analysis.buyZoneFramework.length > 0 && (
                  <div className="pdf-section oi-card p-7 mb-10">
                    <div className="flex justify-between items-center mb-10">
                      <SectionHeader
                        icon={Target}
                        title="Buy Zone Framework"
                        className="mb-0 border-b-0"
                      />
                      {dcfModel?.intrinsicValuePerShare && (
                        <div className="flex items-center gap-2 px-3 py-1 bg-surface border border-ink-200 rounded-md shadow-sm">
                          <div
                            className={cn(
                              "w-2 h-2 rounded-full",
                              analysis.buyZoneFramework.every((item) => {
                                const val = parseFloat(
                                  item.threshold.replace(/[^0-9.]/g, ""),
                                );
                                return (
                                  isNaN(val) ||
                                  val <= dcfModel.intrinsicValuePerShare * 1.05
                                );
                              })
                                ? "bg-success-600"
                                : "bg-warning-600",
                            )}
                          />
                          <span className="text-[9px] font-medium uppercase tracking-widest text-ink-500">
                            {analysis.buyZoneFramework.every((item) => {
                              const val = parseFloat(
                                item.threshold.replace(/[^0-9.]/g, ""),
                              );
                              return (
                                isNaN(val) ||
                                val <= dcfModel.intrinsicValuePerShare * 1.05
                              );
                            })
                              ? "Mathematically Consistent"
                              : "Valuation Variance Detected"}
                          </span>
                        </div>
                      )}
                    </div>

                    {analysis.buyZoneJustification && (
                      <div className="mb-10 p-6 bg-warning-50 border border-warning-100 rounded-md">
                        <div className="text-[10px] font-medium uppercase tracking-widest text-warning-600 mb-2">
                          Valuation Justification
                        </div>
                        <p className="text-sm text-warning-700 leading-relaxed">
                          {analysis.buyZoneJustification}
                        </p>
                      </div>
                    )}
                    {dcfModel?.intrinsicValuePerShare !== undefined &&
                    dcfModel.intrinsicValuePerShare <= 0 ? (
                      <div className="p-8 bg-danger-50 border border-danger-100 rounded-lg flex items-start gap-4">
                        <div className="shrink-0 mt-1">
                          <XCircle className="w-6 h-6 text-danger-600" />
                        </div>
                        <div>
                          <div className="text-[10px] font-medium uppercase tracking-[0.2em] text-danger-700 mb-2">
                            {analysis.isType5DataContaminated
                              ? "Valuation Not Available"
                              : "Structurally Uninvestable"}
                          </div>
                          <p className="text-sm text-danger-700 leading-relaxed">
                            {analysis.isType5DataContaminated
                              ? "This company was recently spun off from its parent. Available financial data reflects the parent's consolidated operations and cannot be used to estimate intrinsic value. No entry price can be calculated until 2–3 years of standalone audited filings are available. The PASS verdict reflects this data limitation, not a judgment on business quality."
                              : `Intrinsic value is negative ($${dcfModel.intrinsicValuePerShare.toFixed(2)} per share), driven by negative normalized owner earnings. No valid entry price exists at any discount. Do not allocate capital at any nominal equity price.`}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                        {dcfModel?.buyUnderPrice &&
                          (() => {
                            const isTriggered =
                              analysis.currentPrice < dcfModel.buyUnderPrice;
                            return (
                              <div
                                className={cn(
                                  "p-5 sm:p-8 rounded-lg border shadow-sm relative overflow-hidden group/card",
                                  isTriggered
                                    ? "bg-oxblood-50 border-oxblood/30"
                                    : "bg-surface-2 border-ink-200",
                                )}
                              >
                                <div className="absolute -right-4 -top-4 opacity-10 group-hover/card:scale-110 transition-transform">
                                  <ShieldCheck
                                    className={cn(
                                      "w-24 h-24",
                                      isTriggered
                                        ? "text-oxblood"
                                        : "text-ink-500",
                                    )}
                                  />
                                </div>
                                <div
                                  className={cn(
                                    "text-[10px] font-medium uppercase tracking-[0.2em] mb-1",
                                    isTriggered
                                      ? "text-oxblood"
                                      : "text-ink-500",
                                  )}
                                >
                                  Dashboard Buy Under
                                </div>
                                {isTriggered && (
                                  <div className="text-[9px] font-medium uppercase tracking-widest text-oxblood mb-3">
                                    ● Triggered
                                  </div>
                                )}
                                {!isTriggered && <div className="mb-3" />}
                                <div
                                  className={cn(
                                    "text-3xl font-mono font-medium mb-2",
                                    isTriggered
                                      ? "text-ink"
                                      : "text-ink",
                                  )}
                                >
                                  ${dcfModel.buyUnderPrice.toFixed(2)}
                                </div>
                                <p className="text-[10px] text-ink-500 leading-relaxed">
                                  Calculated based on Business Reality +{" "}
                                  {dcfModel.effectiveMarginOfSafety}% Margin of
                                  Safety. This is our primary entry trigger.
                                </p>
                              </div>
                            );
                          })()}
                        {analysis.buyZoneFramework.map((item, idx) => {
                          const thresholdVal = parseFloat(
                            item.threshold.replace(/[^0-9.]/g, ""),
                          );
                          const isOvervalued =
                            dcfModel?.intrinsicValuePerShare &&
                            !isNaN(thresholdVal) &&
                            thresholdVal >
                              dcfModel.intrinsicValuePerShare * 1.05;

                          return (
                            <div
                              key={idx}
                              className="p-5 sm:p-8 bg-surface-2 rounded-lg border border-ink-200 shadow-sm relative overflow-hidden group/card items-start flex flex-col"
                            >
                              <div className="text-[10px] font-medium uppercase tracking-[0.2em] text-ink-500 mb-4">
                                {dcfModel?.intrinsicValuePerShare && !isNaN(thresholdVal)
                                  ? `${Math.round((1 - thresholdVal / dcfModel.intrinsicValuePerShare) * 100)}% Discount to IV`
                                  : item.trigger}
                              </div>
                              <div className="flex items-baseline gap-2 mb-2">
                                <div
                                  className={cn(
                                    "text-3xl font-mono font-medium",
                                    isOvervalued
                                      ? "text-warning-600"
                                      : "text-ink",
                                  )}
                                >
                                  {item.threshold}
                                </div>
                                {isOvervalued && (
                                  <Tooltip
                                    content={`This entry trigger is above the calculated intrinsic value ($${dcfModel.intrinsicValuePerShare.toFixed(2)}). Exercise caution.`}
                                  >
                                    <AlertTriangle className="w-4 h-4 text-warning-600" />
                                  </Tooltip>
                                )}
                              </div>
                              <p className="text-[10px] text-ink-500 leading-relaxed h-full">
                                {item.rationale}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {(() => {
                      const tierPrices = (analysis.buyZoneFramework || [])
                        .map((i) => parseFloat(i.threshold.replace(/[^0-9.]/g, "")))
                        .filter((v) => !isNaN(v));
                      const deepestTier = tierPrices.length
                        ? Math.min(...tierPrices)
                        : NaN;
                      if (
                        !dcfModel?.buyUnderPrice ||
                        isNaN(deepestTier) ||
                        dcfModel.buyUnderPrice >= deepestTier
                      )
                        return null;
                      return (
                        <p className="mt-8 text-[10px] text-ink-500 leading-relaxed italic">
                          {/* (Tuning v2.94 F-6) Old wording claimed name-specific risk
                              calibration; the requirement is currently base MOS + market-wide
                              macro + quality scorecard (risk-tier add-ons are flag-gated OFF
                              pending RF-2). State what the formula actually does. */}
                          The discount markers above are generic %-to-IV reference
                          points, not entry recommendations. This name's primary entry
                          trigger reflects a {dcfModel.effectiveMarginOfSafety}% required
                          margin of safety — wider than the deepest reference marker. The
                          requirement is set by the platform's entry discipline: a base
                          margin of safety, widened under elevated market-wide valuations,
                          and adjusted by the quality scorecard where applicable.
                        </p>
                      );
                    })()}
                  </div>
                )}

              {/* Deep Valuation Insights */}
              <div className="pdf-section oi-card p-7 mb-10">
                <SectionHeader
                  icon={Info}
                  title="Deep Valuation Insights"
                  className="mb-4"
                />
                <div className="text-lg text-ink-600 leading-relaxed whitespace-pre-wrap">
                  {stripValuationGap(analysis.valuationInsights)}
                </div>
              </div>
            </div>

            {/* 2. Business Quality (Consolidated) */}
            <div className="mb-10 border-t border-ink-200 pt-10">
              <div className="pdf-section pdf-keep-with-next mb-8">
                <h4 className="text-2xl font-serif font-medium italic text-ink">
                  Business Quality
                </h4>
              </div>

              {analysis.unitEconomics && analysis.unitEconomics.length > 0 && (
                <div className="pdf-section mb-10 oi-card p-7">
                  <SectionHeader icon={Activity} title="Unit Economics" />
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {/* [SPEC-1] Deterministic current market multiple, engine-computed
                        (price ÷ tangible book/sh). Rendered as chrome so the figure has a
                        single source and prose never derives it. */}
                    {type4CurrentPtbv != null && (
                      <div className="p-6 bg-surface-2 rounded-lg border border-ink-200">
                        <div className="text-[10px] font-medium uppercase tracking-widest text-ink-500 mb-2">
                          Current P/TBV
                        </div>
                        <div className="text-2xl font-mono font-medium text-ink mb-2">
                          {`${Number(type4CurrentPtbv).toFixed(2)}x`}
                        </div>
                        <div className="text-[9px] text-ink-500 italic">
                          {type4RiImpliedPtbv != null
                            ? `Price ÷ tangible book/sh. RI-implied fair: ${Number(type4RiImpliedPtbv).toFixed(2)}x`
                            : "Price ÷ tangible book/sh (engine-computed)"}
                        </div>
                      </div>
                    )}
                    {analysis.unitEconomics.map((item, idx) => (
                      <div
                        key={idx}
                        className="p-6 bg-surface-2 rounded-lg border border-ink-200"
                      >
                        <div className="text-[10px] font-medium uppercase tracking-widest text-ink-500 mb-2">
                          {item.label}
                        </div>
                        <div className="text-2xl font-mono font-medium text-ink mb-2">
                          {item.value}
                        </div>
                        {item.benchmark && (
                          <div className="text-[9px] text-ink-500 italic">
                            Benchmark: {item.benchmark}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {analysis.peerBenchmarking &&
                analysis.peerBenchmarking.length > 0 && (
                  <div className="pdf-section mb-10 oi-card p-7">
                    <SectionHeader icon={BarChart4} title="Peer Benchmarking" />
                    {analysis.peerBenchmarks && analysis.peerBenchmarks.length > 0 && (() => {
                      const fmtPct = (v?: number | null) =>
                        typeof v === "number" && !isNaN(v) ? `${v.toFixed(1)}%` : "—";
                      const fmtX = (v?: number | null) =>
                        typeof v === "number" && !isNaN(v) ? `${v.toFixed(1)}x` : "—";

                      // [SPEC-3] Type-4 peer rows carry explicitly named fields
                      // (ptbv / rotce / netMarginPct). Render them under their
                      // OWN labels — the generic ROIC/FCF-Yield/EV-FCF headers below
                      // describe non-Type-4 metrics and previously displayed a Type-4
                      // ROTCE under an "FCF Yield" header. A ROTCE the gate could not
                      // verify is nulled upstream and renders UNVERIFIED, never a number.
                      if (isType4Entity) {
                        const t4rows = [
                          {
                            name: analysis.ticker,
                            isSubject: true,
                            ptbv: type4CurrentPtbv as number | null,
                            rotce: type4SubjectRotcePct as number | null,
                            rotceUnverified: false,
                            netMargin: null as number | null,
                          },
                          ...analysis.peerBenchmarks.map((p: any) => ({
                            name: p.ticker,
                            isSubject: false,
                            ptbv: typeof p.ptbv === "number" ? p.ptbv : null,
                            rotce: typeof p.rotce === "number" ? p.rotce * 100 : null,
                            rotceUnverified: p.rotceUnverified === true,
                            netMargin:
                              typeof p.netMarginPct === "number"
                                ? p.netMarginPct * 100
                                : null,
                          })),
                        ];
                        return (
                          <div className="mb-8 overflow-x-auto">
                            <table className="w-full text-sm tabular-nums border-collapse">
                              <thead>
                                <tr className="text-[10px] uppercase tracking-widest text-ink-500 border-b border-ink-200">
                                  <th className="text-left font-medium py-2 pr-4">Company</th>
                                  <th className="text-right font-medium py-2 px-3">P/TBV</th>
                                  <th className="text-right font-medium py-2 px-3">ROTCE</th>
                                  <th className="text-right font-medium py-2 pl-3">Net Margin</th>
                                </tr>
                              </thead>
                              <tbody>
                                {t4rows.map((r, i) => (
                                  <tr
                                    key={i}
                                    className={cn(
                                      "border-b border-ink-100",
                                      r.isSubject ? "font-medium text-ink bg-oxblood-50" : "text-ink-700",
                                    )}
                                  >
                                    <td className="text-left py-2 pr-4">
                                      {r.name}{r.isSubject ? " (subject)" : ""}
                                    </td>
                                    <td className="text-right py-2 px-3 font-mono">{fmtX(r.ptbv)}</td>
                                    <td className="text-right py-2 px-3 font-mono">
                                      {r.rotceUnverified ? (
                                        <span className="text-[9px] not-italic text-ink-400">
                                          UNVERIFIED
                                        </span>
                                      ) : (
                                        fmtPct(r.rotce)
                                      )}
                                    </td>
                                    <td className="text-right py-2 pl-3 font-mono">{fmtPct(r.netMargin)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            <p className="mt-2 text-[10px] text-ink-400 italic">
                              Type 4 (financial) metrics. Subject P/TBV is price ÷ tangible book/sh and subject
                              ROTCE is the engine&apos;s residual-income ROTCE; peer ROTCE and net margin
                              come from the FMP pipeline, so peer and subject figures are not strictly like-for-like.
                              Net margin is net income ÷ revenue — it is not a combined ratio; the subject&apos;s
                              combined ratio appears in the underwriting section and has no per-peer equivalent here.
                              UNVERIFIED = the peer gate could not confirm the value; it is not comparable.
                            </p>
                          </div>
                        );
                      }

                      // Subject metrics (oracleMetrics) are already percentages; peer
                      // metrics (peerBenchmarks) are raw FMP decimals → ×100 here.
                      const rows = [
                        {
                          name: analysis.ticker,
                          isSubject: true,
                          roic: analysis.oracleMetrics?.roic ?? null,
                          opMargin: analysis.oracleMetrics?.operatingMargin ?? null,
                          fcfYield: analysis.oracleMetrics?.fcfYield ?? null,
                          evFcf: null as number | null,
                          intCov: analysis.oracleMetrics?.interestCoverage ?? null,
                        },
                        ...analysis.peerBenchmarks.map((p) => ({
                          name: p.ticker,
                          isSubject: false,
                          roic: p.roic != null ? p.roic * 100 : null,
                          opMargin: p.operatingMargin != null ? p.operatingMargin * 100 : null,
                          fcfYield: p.fcfYield != null ? p.fcfYield * 100 : null,
                          evFcf: p.pFcf ?? null,
                          intCov: p.interestCoverage ?? null,
                        })),
                      ];
                      return (
                        <div className="mb-8 overflow-x-auto">
                          <table className="w-full text-sm tabular-nums border-collapse">
                            <thead>
                              <tr className="text-[10px] uppercase tracking-widest text-ink-500 border-b border-ink-200">
                                <th className="text-left font-medium py-2 pr-4">Company</th>
                                <th className="text-right font-medium py-2 px-3">ROIC</th>
                                <th className="text-right font-medium py-2 px-3">Op Margin</th>
                                <th className="text-right font-medium py-2 px-3">FCF Yield</th>
                                <th className="text-right font-medium py-2 px-3">EV/FCF</th>
                                <th className="text-right font-medium py-2 pl-3">Int. Cov.</th>
                              </tr>
                            </thead>
                            <tbody>
                              {rows.map((r, i) => (
                                <tr
                                  key={i}
                                  className={cn(
                                    "border-b border-ink-100",
                                    r.isSubject ? "font-medium text-ink bg-oxblood-50" : "text-ink-700",
                                  )}
                                >
                                  <td className="text-left py-2 pr-4">
                                    {r.name}{r.isSubject ? " (subject)" : ""}
                                  </td>
                                  <td className="text-right py-2 px-3 font-mono">{fmtPct(r.roic)}</td>
                                  <td className="text-right py-2 px-3 font-mono">{fmtPct(r.opMargin)}</td>
                                  <td className="text-right py-2 px-3 font-mono">{fmtPct(r.fcfYield)}</td>
                                  <td className="text-right py-2 px-3 font-mono">{fmtX(r.evFcf)}</td>
                                  <td className="text-right py-2 pl-3 font-mono">{fmtX(r.intCov)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          <p className="mt-2 text-[10px] text-ink-400 italic">
                            Deterministic figures from the FMP pipeline. EV/FCF and interest coverage shown where available; subject EV/FCF omitted.
                          </p>
                        </div>
                      );
                    })()}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-y-6 gap-x-0">
                      {Object.entries(
                        analysis.peerBenchmarking.reduce(
                          (acc, item) => {
                            if (!acc[item.peer]) acc[item.peer] = [];
                            acc[item.peer].push(item);
                            return acc;
                          },
                          {} as Record<
                            string,
                            typeof analysis.peerBenchmarking
                          >,
                        ),
                      ).map(([peer, items], peerIdx, arr) => (
                        <div
                          key={peerIdx}
                          className={cn(
                            "space-y-3 pb-6",
                            peerIdx < arr.length - 1
                              ? "border-b border-ink-200"
                              : "",
                            Math.floor(peerIdx / 3) <
                              Math.floor((arr.length - 1) / 3)
                              ? "md:border-b md:border-ink-200"
                              : "md:border-b-0",
                            peerIdx % 3 === 0
                              ? peerIdx !== arr.length - 1
                                ? "md:border-r md:border-ink-200 md:pr-6"
                                : "md:pr-6"
                              : peerIdx % 3 === 1
                                ? peerIdx !== arr.length - 1
                                  ? "md:border-r md:border-ink-200 md:px-6"
                                  : "md:px-6"
                                : "md:pl-6",
                          )}
                        >
                          <h5 className="text-xs font-medium uppercase tracking-[0.2em] text-ink-500 border-b border-ink-200 pb-2">
                            {peer}
                          </h5>
                          <div className="space-y-4">
                            {items.map((item, idx) => {
                              const isLongComparison =
                                item.comparison.length > 15;
                              const comparisonText = isLongComparison
                                ? "NOTE"
                                : item.comparison;
                              const contextText =
                                item.context ||
                                (isLongComparison ? item.comparison : null);
                              const isPositive =
                                item.comparison
                                  .toLowerCase()
                                  .includes("better") ||
                                item.comparison
                                  .toLowerCase()
                                  .includes("favorable") ||
                                item.comparison
                                  .toLowerCase()
                                  .includes("higher") ||
                                (item.comparison
                                  .toLowerCase()
                                  .includes("lower") &&
                                  !item.comparison
                                    .toLowerCase()
                                    .includes("risk"));
                              return (
                                <div
                                  key={idx}
                                  className="flex flex-col gap-1 pb-3 border-b border-ink-200 last:border-0 last:pb-0"
                                >
                                  <div className="flex justify-between items-start gap-2 mb-1">
                                    <div className="text-[10px] font-medium text-ink leading-tight pr-2 uppercase tracking-wide">
                                      {item.metric}
                                    </div>
                                    <div className="text-sm font-mono font-medium text-ink leading-tight shrink-0">
                                      {item.value}
                                    </div>
                                  </div>
                                  <div className="flex flex-col items-end gap-1.5 w-full">
                                    {!isLongComparison && (
                                      <div
                                        className={cn(
                                          "text-[9px] font-medium uppercase tracking-widest px-2 py-0.5 rounded inline-block text-center",
                                          isPositive
                                            ? "bg-success-50 text-success-700"
                                            : "bg-warning-50 text-warning-700",
                                        )}
                                      >
                                        {comparisonText}
                                      </div>
                                    )}
                                    {contextText && (
                                      <div
                                        className={cn(
                                          "text-[8px] font-medium uppercase tracking-widest px-2 py-1.5 rounded text-right whitespace-normal break-words w-full leading-relaxed",
                                          isPositive
                                            ? "bg-success-50 text-success-700"
                                            : "bg-warning-50 text-warning-700",
                                        )}
                                      >
                                        {contextText}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              {analysis.businessQualityVsInvestmentAttractiveness && (
                <div className="pdf-section mb-10">
                  <SectionHeader
                    icon={Layers}
                    title="Business Quality vs. Investment Attractiveness"
                    className="mb-6"
                  />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="oi-card p-6">
                      <div className="text-[10px] font-medium uppercase tracking-widest text-ink-500 mb-6">
                        Business Quality
                      </div>
                      <p className="text-lg text-ink-600 leading-relaxed">
                        {
                          analysis.businessQualityVsInvestmentAttractiveness
                            .businessQuality
                        }
                      </p>
                    </div>
                    <div className="p-6 bg-oxblood text-paper rounded-md">
                      <div className="text-[10px] font-medium uppercase tracking-widest text-paper/60 mb-6">
                        Investment Attractiveness
                      </div>
                      <p className="text-lg text-paper leading-relaxed">
                        {
                          analysis.businessQualityVsInvestmentAttractiveness
                            .investmentAttractiveness
                        }
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {analysis.archetype && (
                <DarkEmphasisCard icon={Award} title="Investment Archetype">
                  <div className="text-3xl font-serif font-medium italic mb-6">
                    {analysis.archetype.category}
                  </div>
                  <p className="text-lg text-ink-600 leading-relaxed">
                    {analysis.archetype.rationale}
                  </p>
                </DarkEmphasisCard>
              )}

              {analysis.moatAnalysis && (
                <OracleDetailSection
                  title="Economic Moat Details"
                  icon={Shield}
                  data={analysis.moatAnalysis}
                />
              )}
              {analysis.oracleDetails?.competence && (
                <OracleDetailSection
                  title="Circle of Competence Details"
                  icon={Target}
                  data={analysis.oracleDetails.competence}
                />
              )}
              {analysis.oracleDetails?.management && (
                <OracleDetailSection
                  title="Management Assessment Details"
                  icon={Award}
                  data={analysis.oracleDetails.management}
                />
              )}
              {analysis.oracleDetails?.predictability && (
                <OracleDetailSection
                  title="Long-Term Predictability Details"
                  icon={History}
                  data={analysis.oracleDetails.predictability}
                />
              )}
              {analysis.oracleDetails?.stability && (
                <OracleDetailSection
                  title="Business Stability Details"
                  icon={Activity}
                  data={analysis.oracleDetails.stability}
                />
              )}

              {/* Narrative Blocks Grouped */}
              <NarrativeGrid>
                {analysis.normalizedEarnings && (
                  <StackedNarrativeCard
                    title="Normalized Earnings"
                    icon={DollarSign}
                    value={
                      !isNaN(Number(analysis.normalizedEarnings))
                        ? formatCurrencyValue(
                            Number(analysis.normalizedEarnings),
                          )
                        : analysis.normalizedEarnings
                    }
                    content="Estimated steady-state earnings power excluding one-time items and cyclical peaks."
                  />
                )}
                {/* [SPEC-2] Investment engine sized from the filed balance sheet and
                    labeled by composition. The Historian's investmentPortfolioSize is
                    descriptive context only — it mislabeled total investments (incl. the
                    cash/T-bill pile) as an "equity portfolio". */}
                {isType4Entity && type4InvestmentComposition && (
                  <StackedNarrativeCard
                    title="Investment Engine"
                    icon={DollarSign}
                    value={
                      type4InvestmentComposition.totalInvestments != null
                        ? formatCurrencyValue(type4InvestmentComposition.totalInvestments)
                        : "—"
                    }
                    content={(() => {
                      // [SPEC-8] The headline total and the two sub-lines are each
                      // individually deterministic and correct, but they are three
                      // independent FMP balance-sheet fields with overlapping boundaries
                      // — cash & short-term and long-term investments are NOT a partition
                      // of total investments and can sum past it (BRK-B: 373.3 + 336.4 =
                      // 709.7 against a 657.84 total). Stacked silently under one total
                      // that reads as "the parts exceed the whole". Label them as
                      // non-additive rather than reconcile: no residual is synthesized
                      // and no clean split is invented, because FMP does not give us one.
                      const parts: string[] = [];
                      if (type4InvestmentComposition.cashAndShortTerm != null) {
                        parts.push(
                          `Cash & short-term investments: ${formatCurrencyValue(type4InvestmentComposition.cashAndShortTerm)}`,
                        );
                      }
                      if (type4InvestmentComposition.longTermEquityAndOther != null) {
                        parts.push(
                          `Long-term equity & other investments: ${formatCurrencyValue(type4InvestmentComposition.longTermEquityAndOther)}`,
                        );
                      }
                      const nonAdditiveNote =
                        parts.length > 1
                          ? ` These are distinct balance-sheet lines with overlapping boundaries;` +
                            ` they are not components that sum to the total and may exceed it.`
                          : ` This is a distinct balance-sheet line whose boundaries overlap the` +
                            ` total; it is not a component of it.`;
                      return (
                        `Total investments per the filed balance sheet — not an equity-only portfolio.` +
                        (parts.length > 0 ? ` ${parts.join(". ")}.${nonAdditiveNote}` : "") +
                        ` Engine-computed; lines shown only where the balance sheet carries them.`
                      );
                    })()}
                  />
                )}
                {analysis.throughCycleAssumptions && (
                  <StackedNarrativeCard
                    title="Through Cycle Assumptions"
                    icon={RotateCcw}
                    content={analysis.throughCycleAssumptions}
                  />
                )}
                {analysis.longTermOutlook && (
                  <StackedNarrativeCard
                    title="10-Year Outlook"
                    icon={TrendingUp}
                    content={analysis.longTermOutlook}
                  />
                )}
                {analysis.tenYearHistoricalContext && (
                  <StackedNarrativeCard
                    title="10-Year Historical Context"
                    icon={History}
                    content={analysis.tenYearHistoricalContext}
                  />
                )}
                {analysis.historicalFailuresContext && (
                  <StackedNarrativeCard
                    title="Historical Failures"
                    icon={XCircle}
                    content={analysis.historicalFailuresContext}
                  />
                )}
                {analysis.technicalDifferentiation && (
                  <StackedNarrativeCard
                    title="Technical Differentiation"
                    icon={Zap}
                    content={analysis.technicalDifferentiation}
                  />
                )}
                {analysis.optionalityMultiplier && (
                  <StackedNarrativeCard
                    title="Optionality Multiplier"
                    icon={Zap}
                    value={
                      analysis.optionalityMultiplier.hasHighOptionality
                        ? "High Optionality"
                        : "Limited Optionality"
                    }
                    content={analysis.optionalityMultiplier.description}
                  />
                )}
                {analysis.lollapaloozaEffects && (
                  <StackedNarrativeCard
                    title="Lollapalooza Effects"
                    icon={Zap}
                    content={analysis.lollapaloozaEffects}
                  />
                )}
                {analysis.whyNotPerfect && (
                  <StackedNarrativeCard
                    title="Why This Is Not A Perfect Business"
                    icon={Skull}
                    content={analysis.whyNotPerfect}
                  />
                )}
              </NarrativeGrid>

              {/* Market Gap */}
              {analysis.marketBeliefVsReality &&
                analysis.marketBeliefVsReality.length > 0 && (
                  <div className="pdf-section mb-10 oi-card p-7">
                    <SectionHeader
                      icon={Layers}
                      title="The Market Gap: Belief vs. Reality"
                    />
                    <div className="space-y-8">
                      {analysis.marketBeliefVsReality.map((item, idx) => (
                        <div
                          key={idx}
                          className="grid grid-cols-1 md:grid-cols-2 gap-0 items-stretch rounded-md overflow-hidden border border-ink-200 shadow-md"
                        >
                          <div className="p-5 sm:p-10 bg-surface flex flex-col justify-center border-b md:border-b-0 md:border-r border-ink-200">
                            <div className="text-[10px] font-medium uppercase tracking-[0.3em] text-ink-500 mb-6 flex items-center gap-3">
                              <div className="w-1.5 h-1.5 bg-ink-300 rounded-full" />
                              Market Belief
                            </div>
                            <p className="text-xl text-ink-600 leading-relaxed font-medium">
                              "{coerceToText(item.belief)}"
                            </p>
                          </div>
                          <div
                            className="p-5 sm:p-10 bg-oxblood text-paper flex flex-col justify-center relative overflow-hidden"
                            style={{
                              backgroundColor: "#6E1D24",
                              color: "#FDFBF6",
                            }}
                          >
                            <div className="text-[10px] font-medium uppercase tracking-[0.3em] text-paper/60 mb-6 flex items-center gap-3">
                              <div className="w-1.5 h-1.5 bg-paper/50 rounded-full" />
                              The Reality
                            </div>
                            <p className="text-xl text-paper leading-relaxed font-medium relative z-10">
                              "{coerceToText(item.reality)}"
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
            </div>

            {/* 3. Risks (Consolidated) */}
            <div className="mb-10 border-t border-ink-200 pt-10">
              <div className="pdf-section pdf-keep-with-next mb-8">
                <h4 className="text-2xl font-serif font-medium italic text-ink">
                  Risks & Mitigations
                </h4>
              </div>

              {/* [SPEC-1] Deterministic multiples strip: the current market multiple and
                  the RI-implied fair multiple, both engine-computed. Rendered as chrome so
                  the margin-of-safety discussion cites a single source and prose never
                  derives a P/TBV ratio. */}
              {type4CurrentPtbv != null && (
                <div className="pdf-section mb-6 oi-card p-5 flex flex-wrap gap-8">
                  <div>
                    <div className="text-[9px] font-medium uppercase tracking-widest text-ink-500 mb-1">
                      Current P/TBV
                    </div>
                    <div className="text-lg font-mono font-medium text-ink">
                      {`${Number(type4CurrentPtbv).toFixed(2)}x`}
                    </div>
                  </div>
                  {type4RiImpliedPtbv != null && (
                    <div>
                      <div className="text-[9px] font-medium uppercase tracking-widest text-ink-500 mb-1">
                        RI-Implied Fair P/TBV
                      </div>
                      <div className="text-lg font-mono font-medium text-ink">
                        {`${Number(type4RiImpliedPtbv).toFixed(2)}x`}
                      </div>
                    </div>
                  )}
                  <div className="text-[9px] text-ink-400 italic self-end">
                    Engine-computed: price ÷ tangible book/sh.
                  </div>
                </div>
              )}

              {analysis.oracleDetails?.marginOfSafety && (
                <OracleDetailSection
                  title="Margin of Safety Details"
                  icon={Scale}
                  data={analysis.oracleDetails.marginOfSafety}
                />
              )}

              {analysis.keyRisks && analysis.keyRisks.length > 0 && (
                <div className="pdf-section mb-10 oi-card p-7">
                  <SectionHeader
                    icon={AlertTriangle}
                    title="Risk Mitigation Details"
                  />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {analysis.keyRisks.map((risk, idx) => (
                      <div
                        key={idx}
                        className="p-6 bg-surface-2 rounded-md border border-ink-200"
                      >
                        <div className="flex items-center justify-between mb-6">
                          <div className="text-[10px] font-medium uppercase tracking-widest text-ink-500">
                            {coerceToText(risk.title)}
                          </div>
                          <span
                            className={cn(
                              "px-3 py-1 rounded-lg text-[9px] font-medium uppercase tracking-widest",
                              risk.severity === "high"
                                ? "bg-danger-50 text-danger-600"
                                : risk.severity === "medium"
                                  ? "bg-warning-50 text-warning-600"
                                  : "bg-success-50 text-success-600",
                            )}
                          >
                            {coerceToText(risk.severity)} Risk
                          </span>
                        </div>
                        <p className="text-base text-ink leading-relaxed">
                          {coerceToText(risk.description)}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {analysis.riskModeling && analysis.riskModeling.length > 0 && !analysis.isType5DataContaminated && !analysis.isUnresolvableValuation && (
                <div className="pdf-section mb-10 oi-card p-7">
                  <SectionHeader
                    icon={Target}
                    title="Quantitative Risk Modeling"
                  />
                  <div className="overflow-x-auto print:overflow-x-visible">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="border-b-2 border-ink text-[9px] sm:text-[10px] font-medium uppercase tracking-widest text-ink-500">
                          <th className="py-3 pr-2">Risk Factor</th>
                          <th className="py-3 px-1 text-center">Prob.</th>
                          <th className="py-3 px-1 text-center">Impact</th>
                          <th className="py-3 px-1 text-right">IV Delta</th>
                          <th className="py-3 px-1 text-center">IV $/Share</th>
                          <th className="py-3 pl-1 text-right">Risk-Adj IV</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-ink-200">
                        {analysis.riskModeling.map((item, idx) => (
                          <tr key={idx}>
                            <td className="py-3 pr-2 text-xs sm:text-sm leading-snug">
                              {item.risk}
                            </td>
                            <td className="py-3 px-1 text-center text-xs sm:text-sm font-mono whitespace-nowrap">
                              {item.probability}
                            </td>
                            <td className="py-3 px-1 text-center text-xs sm:text-sm font-mono whitespace-nowrap">
                              {item.impact}
                            </td>
                            <td className="py-3 px-1 text-right text-xs sm:text-sm font-mono font-medium text-danger-600 whitespace-nowrap">
                              {item.ivChange}
                            </td>
                            <td className="py-3 px-1 text-center text-xs sm:text-sm font-mono font-medium text-danger-600 whitespace-nowrap">
                              {item.ivImpactPerShare}
                            </td>
                            <td className="py-3 pl-1 text-right text-xs sm:text-sm font-mono font-medium text-ink whitespace-nowrap">
                              {item.riskAdjustedIV}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {analysis.mistakePrevention &&
                analysis.mistakePrevention.length > 0 && (
                  <div className="pdf-section mb-10 p-7 bg-warning-50 rounded-md border border-warning-100">
                    <SectionHeader
                      icon={AlertCircle}
                      title="Mistake Prevention (Pre-Mortem Analysis)"
                      className="!text-warning-700"
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                      {analysis.mistakePrevention.map((mistake, idx) => (
                        <div key={idx} className="space-y-4">
                          <div className="text-[10px] font-medium uppercase tracking-[0.2em] text-warning-700">
                            {coerceToText(mistake.title)}
                          </div>
                          <p className="text-base text-warning-700 leading-relaxed">
                            {coerceToText(mistake.description)}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              {analysis.whatWouldBreakThisFast &&
                analysis.whatWouldBreakThisFast.length > 0 && (
                  <DarkEmphasisCard
                    icon={Zap}
                    title="What Would Break This Fast (Thesis Killers)"
                    className="border-l-4 border-l-[var(--color-danger-600)]"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 relative z-10">
                      {analysis.whatWouldBreakThisFast.map((item, idx) => (
                        <div
                          key={idx}
                          className="flex gap-4 items-start p-6 bg-surface-2 rounded-md border border-ink-200"
                        >
                          <div className="w-6 h-6 rounded-full bg-danger-50 flex items-center justify-center shrink-0 mt-1">
                            <span className="text-[10px] font-medium text-danger-600">
                              {idx + 1}
                            </span>
                          </div>
                          <p className="text-base text-ink-600 leading-relaxed">
                            {coerceToText(item)}
                          </p>
                        </div>
                      ))}
                    </div>
                  </DarkEmphasisCard>
                )}

              {analysis.upsideCase && (
                <div className="pdf-section mb-10 p-7 bg-success-50 rounded-md border border-success-100">
                  <SectionHeader
                    icon={TrendingUp}
                    title="The Upside Case"
                    className="!text-success-700"
                  />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-4">
                      <div className="text-[10px] font-medium uppercase tracking-[0.2em] text-success-700">
                        Scenario
                      </div>
                      <p className="text-xl text-success-700 font-medium">
                        {coerceToText(analysis.upsideCase.scenario)}
                      </p>
                    </div>
                    <div className="space-y-4">
                      <div className="text-[10px] font-medium uppercase tracking-[0.2em] text-success-700">
                        Impact & Probability
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-lg text-success-700">
                          {coerceToText(analysis.upsideCase.impact)}
                        </div>
                        <span className="px-3 py-1 bg-success-600 text-paper text-[9px] font-medium uppercase tracking-widest rounded-md">
                          {coerceToText(analysis.upsideCase.probability)} Prob.
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Oracle Principle Section */}
            {analysis.oraclePrinciple && (
              <div className="pdf-section mb-10 text-center py-10 border-y border-ink-200">
                <div className="text-[10px] font-medium uppercase tracking-[0.6em] text-ink-500 mb-10">
                  The Oracle Principle
                </div>
                <p className="text-3xl font-medium text-ink leading-tight max-w-3xl mx-auto tracking-tight">
                  "{coerceToText(analysis.oraclePrinciple)}"
                </p>
              </div>
            )}
          </div>

          {(analysis.pulse || analysis.analystIntelligence) && (
            <div className="pdf-section">
              <MarketIntelligencePanel
                pulse={analysis.pulse}
                intelligence={analysis.analystIntelligence}
                variant="memo"
              />
            </div>
          )}

          {/* Disclaimer Section */}
          <div className="pdf-section pt-4 pb-12 mt-8 border-t border-ink-200">
            <div className="flex flex-col gap-4 text-[9px] font-medium uppercase tracking-[0.3em] text-ink-500 leading-relaxed">
              <div className="flex items-center gap-4">
                <Info className="w-4 h-4 shrink-0" />
                <span>
                  Valuations are derived from deterministic financial models. AI
                  assists with qualitative synthesis.
                </span>
              </div>
              <div className="flex items-center gap-4 text-danger-600 bg-danger-50 p-4 rounded-md border border-danger-100">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                <span className="text-danger-700">
                  This is not investment advice. Consult a licensed financial
                  advisor before making any investment decisions.
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile PDF export — inline, after memo content, no fixed positioning */}
        <div className="sm:hidden mt-6">
          <button
            onClick={onExport}
            disabled={isExporting}
            className="w-full bg-oxblood text-paper py-4 rounded-md font-medium text-sm uppercase tracking-widest flex items-center justify-center gap-3 active:scale-[0.98] transition-all disabled:opacity-50"
          >
            {isExporting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Generating PDF...
              </>
            ) : (
              <>
                <Download className="w-4 h-4" />
                Download PDF Memo
              </>
            )}
          </button>
        </div>
      </section>
    </>
  );
}
